<template>
  <div class="subscription-teams">
    <!-- Title -->
    <app-title-bar>
      <app-material-icon name="group" />
      <h2>{{ $t('subscriptions.views.SubscriptionTeams.title') }}</h2>
    </app-title-bar>

    <div class="d-flex flex-column row-gap-4 layout-spacing">
      <!-- Table -->
      <template v-if="subscriptionTeamsError">
        <app-alert-in-page variant="error"
                          no-icon
                          :dismissible="false"
        >
          {{ $t('subscriptions.views.SubscriptionTeams.getTeamsError') }}
        </app-alert-in-page>
      </template>
      <template v-else>
        <template v-if="subscriptionTeamsPending">
          <AppDataTableSkeletonLoader class="w-100"/>
        </template>
        <template v-else>
          <template v-if="subscriptionTeams.length">
            <div class="d-flex align-center">
              <SearchTextField v-model="searchQuery"/>
              <app-button
                class="ml-auto"
                @click="openSubscriptionPane"
              >
                  {{ $t('subscriptions.views.SubscriptionTeams.newTeam') }}
              </app-button>
            </div>
            <v-data-table ref="SubscriptionTeamsTable"
                          class="SubscriptionTeams-table"
                          :items="filteredSubscriptionTeams"
                          :headers="headers"
                          :page="currentPage"
                          :items-per-page="itemsPerPage"
                          @click:row="handleRowClick"
            >
              <template #item.name="{item}">
                <AddTeamComboboxItem class="td-mobile-view" :item="item"></AddTeamComboboxItem>
              </template>
              <template #item.members="{item}">
                <i18n-t keypath="subscriptions.views.SubscriptionTeams.memberCount" tag="span" :plural="item.members.length">
                  <template v-slot:count>
                    {{ item.members.length }}
                  </template>
                </i18n-t>
              </template>
              <template #item.actions="{item}">
                <div class="td-mobile-view">
                  <SubscriptionTeamsTableItemMenu
                    @open-team-edit-pane="onRowClick(item)"
                    @delete-team="openDeleteTeamDialog(item)"
                  />
                </div>
              </template>

              <template #bottom>
                <div class="d-flex justify-end mt-4">
                  <AppPagination v-model:current-page="currentPage"
                                v-model:page-size="itemsPerPage"
                                :pageSizes="ITEMS_PER_PAGE"
                                :total="filteredSubscriptionTeams.length"
                  />
                </div>
              </template>
            </v-data-table>
          </template>
          <div v-else class="d-flex flex-column justify-center align-center"
                      style="margin-top: 10%;"
          >
            <div class="mb-8">
              <v-img src="@/assets/img/subscription/no-teams-illustration.svg" width="325" />
            </div>
            <p class="mb-6 text--small">
              {{ $t('subscriptions.views.SubscriptionTeams.emptyStateText') }}
            </p>
            <app-button
              @click="openSubscriptionPane"
            >
              {{ $t('subscriptions.views.SubscriptionTeams.newTeam') }}
            </app-button>
          </div>
        </template>
      </template>
    </div>

    <SubscriptionTeamsPane v-if="subscriptionPaneIsOpen" @close="closeSubscriptionPane" />
    <SubscriptionTeamsDeleteDialog v-if="deleteTeamDialog.isOpen"
      :subscriptionTeam="deleteTeamDialog.subscriptionTeam"
      @close="onCloseDeleteTeamDialog"
      @delete-success="OnDeleteSuccessDeleteTeamDialog"
    />
  </div>
</template>

<script>
  import { cloneDeep } from 'lodash-es'
  import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

  import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
  import AppPagination from '@/common/pagination/AppPagination.vue'
  import { usePagination } from '@/common/pagination/usePagination'
  import SearchTextField from '@/common/SearchTextField.vue'
  import { toast } from '@/common/utils/toast'
  import AddTeamComboboxItem from '@/project/members/AddTeamComboboxItem.vue'
  import { GET_SUBSCRIPTION_TEAMS } from '@/store/modules/subscription/action_types'

  import {
    SET_SUBSCRIPTION_TEAMS_PANE_IS_OPEN,
    SET_SELECTED_SUBSCRIPTION_TEAM,
    SET_DELETE_TEAM_DIALOG,
    REMOVE_SUBSCRIPTION_TEAM,
  } from '../../store/modules/subscription/mutation_types'
  import { SUBSCRIPTION_TEAMS_ITEMS_PER_PAGE } from '../constants.js'
  import SubscriptionTeamsDeleteDialog from '../SubscriptionTeamsDeleteDialog.vue'
  import SubscriptionTeamsPane from '../SubscriptionTeamsPane.vue'
  import SubscriptionTeamsTableItemMenu from '../SubscriptionTeamsTableItemMenu.vue'

  const ITEMS_PER_PAGE = [10, 50, 100]

  export default {
    name: 'SubscriptionTeams',
    components: {
      SearchTextField,
      AppDataTableSkeletonLoader,
      AddTeamComboboxItem,
      SubscriptionTeamsPane,
      SubscriptionTeamsTableItemMenu,
      SubscriptionTeamsDeleteDialog,
      AppPagination,
    },
    setup: () => {
      const store = useStore()
      const { currentPage, itemsPerPage } = usePagination({
        currentUserId: store.getters['user/currentUserId'],
        userSettingsKey: SUBSCRIPTION_TEAMS_ITEMS_PER_PAGE,
        pageSize: ITEMS_PER_PAGE[0],
      })

      return {
        currentPage,
        itemsPerPage,
      }
    },
    data () {
      return {
        searchQuery: '',
        headers: [
          {
            title: this.$t('subscriptions.views.SubscriptionTeams.headers.name'),
            key: 'name',
          },
          {
            title: this.$t('subscriptions.views.SubscriptionTeams.headers.members'),
            key: 'members',
            sortable: false,
          },
          {
            key: 'actions',
            width: '50px',
            sortable: false,
          },
        ],
        ITEMS_PER_PAGE,
      }
    },
    computed: {
      ...mapGetters('user', ['currentUserId']),
      ...mapState('subscription', [
        'subscriptionPaneIsOpen',
        'deleteTeamDialog',
        'subscriptionTeamsPending',
        'subscriptionTeams',
        'subscriptionTeamsError',
      ]),
      filteredSubscriptionTeams () {
        let filteredSubscriptionTeams = cloneDeep(this.subscriptionTeams)
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase()
          filteredSubscriptionTeams = filteredSubscriptionTeams.filter((team) => {
            return team.name.toLowerCase().includes(query)
          })
        }
        return filteredSubscriptionTeams
      },
    },
    created () {
      this.getSubscriptionTeams()
    },
    methods: {
      ...mapMutations('subscription', [
        SET_SUBSCRIPTION_TEAMS_PANE_IS_OPEN,
        SET_SELECTED_SUBSCRIPTION_TEAM,
        SET_DELETE_TEAM_DIALOG,
        REMOVE_SUBSCRIPTION_TEAM,
      ]),
      ...mapActions('subscription', [GET_SUBSCRIPTION_TEAMS]),
      async getSubscriptionTeams () {
        try {
          await this.GET_SUBSCRIPTION_TEAMS()
        } catch (error) {
          toast.error(this.$t('subscriptions.views.SubscriptionTeams.getTeamsError'))
        }
      },
      handleRowClick (event, row) {
        this.onRowClick(row.item)
      },
      onRowClick (team) {
        this.SET_SELECTED_SUBSCRIPTION_TEAM(team)
        this.openSubscriptionPane()
      },
      openSubscriptionPane () {
        this.SET_SUBSCRIPTION_TEAMS_PANE_IS_OPEN(true)
      },
      closeSubscriptionPane () {
        this.SET_SUBSCRIPTION_TEAMS_PANE_IS_OPEN(false)
      },
      openDeleteTeamDialog (team) {
        this.SET_DELETE_TEAM_DIALOG({
          subscriptionTeam: team,
          isOpen: true,
        })
      },
      onCloseDeleteTeamDialog () {
        this.SET_DELETE_TEAM_DIALOG({
          subscriptionTeam: null,
          isOpen: false,
        })
      },
      OnDeleteSuccessDeleteTeamDialog (deletedTeam) {
        this.REMOVE_SUBSCRIPTION_TEAM(this.subscriptionTeams.findIndex((team) => team.id === deletedTeam.id))
      },
    },
  }
</script>

<style scoped lang="scss">
  .subscription-teams {
    :deep(.SubscriptionTeams-table) {
      background-color: transparent;
      table {
        border-collapse: separate;
        border-spacing: 0 5px;
      }
      th {
        color: rgb(var(--v-theme-accent)) !important;
        font-size: 14px !important;
        border-bottom: none !important;
      }
      td {
        color: rgb(var(--v-theme-accent)) !important;
        font-size: 14px !important;
        border-bottom: none !important;
        text-align: left !important;
        vertical-align: center;
        height: 48px !important;
      }
      tbody {
        tr {
          cursor: pointer;
          background-color: #fff;
        }
      }
    }
  }
</style>
